// Helpers for per-user report export (CSV + print-to-PDF)

export function downloadCsv(filename, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function esc(v) {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function buildUserReportCsv(report) {
  const { name, code, baseline, post } = report;
  const lines = [];
  lines.push(`Participant,${esc(name || "Unassigned")}`);
  lines.push(`Access code,${esc(code)}`);
  lines.push(`Workbook / Activity Book usage,${esc(report.workbook_usage || "Not answered")}`);
  lines.push(`Generated,${new Date().toISOString().slice(0, 10)}`);
  lines.push("");
  lines.push([
    "Competency", "Weight %",
    "Baseline raw", "Baseline %", "Baseline level",
    "Post raw", "Post %", "Post level", "Change %",
  ].join(","));
  baseline.competencies.forEach((c, i) => {
    const p = post.competencies[i] || {};
    const change = c.pct != null && p.pct != null ? Math.round((p.pct - c.pct) * 10) / 10 : "";
    lines.push([
      c.label, c.weight,
      c.raw ?? "", c.pct ?? "", c.band ?? "",
      p.raw ?? "", p.pct ?? "", p.band ?? "", change,
    ].map(esc).join(","));
  });
  lines.push([
    "Weighted Total", 100,
    "", baseline.weighted_total, `${baseline.completed_tests}/${baseline.total_tests} sections`,
    "", post.weighted_total, `${post.completed_tests}/${post.total_tests} sections`,
    Math.round((post.weighted_total - baseline.weighted_total) * 10) / 10,
  ].map(esc).join(","));
  return lines.join("\n");
}

export function downloadUserCsv(report) {
  const scope = (report.name || report.code).replace(/\s+/g, "_");
  downloadCsv(`report-${scope}-${new Date().toISOString().slice(0, 10)}.csv`, buildUserReportCsv(report));
}

export function printElement(selector) {
  const el = document.querySelector(selector);
  if (!el) { window.print(); return; }
  el.classList.add("print-target");
  document.body.classList.add("printing");
  const cleanup = () => {
    el.classList.remove("print-target");
    document.body.classList.remove("printing");
    window.removeEventListener("afterprint", cleanup);
  };
  window.addEventListener("afterprint", cleanup);
  window.print();
  setTimeout(cleanup, 1500);
}

export function printReport() {
  printElement("[data-report]");
}

export function printCertificate() {
  printElement("[data-certificate]");
}

export function buildCohortComparisonCsv(rows) {
  // rows: array of reports {name, code, baseline, post}
  const comps = (rows[0]?.baseline?.competencies) || [];
  const header = [
    "Participant", "Access code", "Baseline complete", "Post complete",
    "Baseline total", "Post total", "Total change", "Workbook usage",
  ];
  comps.forEach((c) => {
    header.push(`${c.label} - Baseline %`, `${c.label} - Post %`, `${c.label} - Change %`);
  });
  const lines = [header.map(esc).join(",")];
  rows.forEach((r) => {
    const row = [
      r.name || "Unassigned", r.code,
      `${r.baseline.completed_tests}/${r.baseline.total_tests}`,
      `${r.post.completed_tests}/${r.post.total_tests}`,
      r.baseline.weighted_total, r.post.weighted_total,
      Math.round((r.post.weighted_total - r.baseline.weighted_total) * 10) / 10,
      r.workbook_usage || "Not answered",
    ];
    r.baseline.competencies.forEach((c, i) => {
      const p = r.post.competencies[i] || {};
      const change = c.pct != null && p.pct != null ? Math.round((p.pct - c.pct) * 10) / 10 : "";
      row.push(c.pct ?? "", p.pct ?? "", change);
    });
    lines.push(row.map(esc).join(","));
  });
  return lines.join("\n");
}

export function downloadCohortCsv(rows) {
  downloadCsv(`cohort-comparison-${new Date().toISOString().slice(0, 10)}.csv`, buildCohortComparisonCsv(rows));
}

// Per-folder results export: student name, results, Book of Activities usage, % improvement
export function buildFolderResultsCsv(rows) {
  const comps = (rows[0]?.baseline?.competencies) || [];
  const header = [
    "Student name", "Access code", "Book of Activities usage",
    "Baseline complete", "Post complete",
    "Baseline total", "Post total", "Change (points)", "% improvement",
  ];
  comps.forEach((c) => {
    header.push(
      `${c.label} - Baseline raw`, `${c.label} - Baseline %`, `${c.label} - Baseline level`,
      `${c.label} - Post raw`, `${c.label} - Post %`, `${c.label} - Post level`,
      `${c.label} - Change %`,
    );
  });
  const lines = [header.map(esc).join(",")];
  rows.forEach((r) => {
    const b = r.baseline.weighted_total;
    const p = r.post.weighted_total;
    const diff = Math.round((p - b) * 10) / 10;
    const relIncrease = b > 0 ? `${Math.round((diff / b) * 1000) / 10}%` : "";
    const row = [
      r.name || "Unassigned", r.code, r.workbook_usage || "Not answered",
      `${r.baseline.completed_tests}/${r.baseline.total_tests}`,
      `${r.post.completed_tests}/${r.post.total_tests}`,
      b, p, diff, relIncrease,
    ];
    r.baseline.competencies.forEach((c, i) => {
      const pc = r.post.competencies[i] || {};
      const change = c.pct != null && pc.pct != null ? Math.round((pc.pct - c.pct) * 10) / 10 : "";
      row.push(
        c.raw ?? "", c.pct ?? "", c.band ?? "",
        pc.raw ?? "", pc.pct ?? "", pc.band ?? "",
        change,
      );
    });
    lines.push(row.map(esc).join(","));
  });
  return lines.join("\n");
}

export function downloadFolderResultsCsv(rows, scope) {
  downloadCsv(`results-${scope}-${new Date().toISOString().slice(0, 10)}.csv`, buildFolderResultsCsv(rows));
}
